import { useCallback, useEffect, useState } from 'react';
import { getDiscussion } from '@peruselab/api-client/discussions';
import { countComments } from './discussionTree';

interface Counts {
  readonly key: string;
  readonly drawing: number;
  readonly molecule: number;
}

/**
 * ADR 0016 part C: the Discuss tab's badge -- comments on the open drawing plus its Molecule.
 * Loaded when a drawing is opened or saved, then kept current by the threads themselves
 * (`setDrawingCount` / `setMoleculeCount`), so posting updates the badge without a refetch.
 * Null while there's no saved drawing or the counts haven't loaded.
 */
export function useDiscussionCount(drawingId: string | null, moleculeId: string | null) {
  const key = drawingId && moleculeId ? `${drawingId}:${moleculeId}` : '';
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    if (!drawingId || !moleculeId) return;
    let cancelled = false;
    Promise.all([getDiscussion('molecule-drawing', drawingId), getDiscussion('molecule', moleculeId)])
      .then(([drawing, molecule]) => {
        if (cancelled) return;
        setCounts({ key, drawing: countComments(drawing.comments), molecule: countComments(molecule.comments) });
      })
      // The badge is a hint; the tab itself shows any error.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [drawingId, moleculeId, key]);

  const setDrawingCount = useCallback(
    (drawing: number) =>
      setCounts((current) => ({ key, drawing, molecule: current?.key === key ? current.molecule : 0 })),
    [key]
  );
  const setMoleculeCount = useCallback(
    (molecule: number) =>
      setCounts((current) => ({ key, molecule, drawing: current?.key === key ? current.drawing : 0 })),
    [key]
  );

  const count = counts && counts.key === key && key ? counts.drawing + counts.molecule : null;
  return { count, setDrawingCount, setMoleculeCount };
}
